import {enforceTerrainReliefOrder} from '../lib/terrain-relief.js';
// Campo interpolato stazioni v2 (chkInterpol v1) — port da assets/js/map-legend.js
// Base v1; gli adattamenti della risoluzione pioggia sono documentati sotto.
//
// ARCHITETTURA v2 (deliberata, DIVERSA dalla v1): il campo NON si ridisegna a
// ogni moveend. Viene renderizzato UNA volta su un canvas offscreen georiferito
// su un DOMINIO FISSO (bbox Sicilia allargata [11.8,35.3]-[16.2,39.0],
// 1100×900) e montato come source MapLibre type 'canvas' (animate:false): la
// GPU lo riproietta durante pan/zoom, zero ricalcolo. Si ricalcola SOLO al
// cambio dati/parametro.
//
// Algoritmi portati 1:1 (riferimenti map-legend.js):
// - kernel IDW gaussiano (createLandBilinearLayer._estimateProjectedAt /
//   estimateWeightedSurface): weight = exp(-d²/2σ²)/max(80,d²) con
//   σ = 0.46·raggio, raggio = max(120, min(280, width·0.28)) px,
//   coverage = Σ gaussiane, soglia coverage 0.015,
//   alpha cella = min(0.78, pow(min(1, coverage·1.65), 0.82)·0.76), skip <0.035;
// - griglia campo cell = max(12, min(16, round(width/64))) px + upscale con
//   smoothing per tutti i campi (pioggia: colori per intervalli, bordi sfumati);
// - indice spaziale a bucket con cutoff 3σ + render a blocchi ~10ms (terra);
// - maschera Sicilia: stesso GeoJSON della v1 (limits_IT_regions.geojson,
//   feature reg_name='Sicilia'); il poligono è rasterizzato UNA volta su un
//   canvas-maschera → composite 'destination-in' (terra) / 'destination-out'
//   (mare), equivalente pixel del clip-path SVG #sicily-clip + punch v1;
// - popup click: estimateWeightedSurface (valore stimato + top-4 contributori
//   pesati con share), instradamento terra/mare via point-in-polygon
//   (port di wsPointInRing/wsPointInPolygon/wsFeatureContainsLatLng, map.js);
// - campo MARINE: port di createMarineTemperatureLayer._renderField (stesso
//   kernel, palette ufficiale condivisa con marker e legenda),
//   SENZA animazione onde (fase successiva).
//
// Adattamenti dichiarati:
// - lo spazio pixel del kernel è il canvas-dominio (width=1100) e non il
//   viewport: i parametri diventano FISSI (raggio 280 px-dominio, σ 128.8) e il
//   campo non varia più con lo zoom — conseguenza voluta del dominio fisso;
// - pioggia: celle di 2 px-dominio (~700 m) e regolarizzazione IDW di 0.25 px²,
//   invece di celle di 16 px (~6 km) e 80 px². Il vecchio minimo di distanza
//   attenuava i picchi locali; non rappresentava una risoluzione del sensore.
//   Pioggia locale: al massimo 8 pluviometri entro 3σ (circa 30 km),
//   raggio 60 px-dominio, σ=27.6. Gli zeri validi restano nel calcolo anche
//   se i marker asciutti sono nascosti; senza supporto non si assegna zero.
//   Restano invariati dominio, palette e dati osservati;
// - le righe del dominio sono spaziate in Mercator (com'è riproiettata
//   l'immagine dalla GPU), non linearmente in latitudine;
// - map.distance (haversine Leaflet, R=6371000) → haversine locale identica;
// - i record stazione arrivano dall'evento 'wsv2:stations' (detail.records di
//   stations.js); i sample terra sono ricostruiti dai row grezzi
//   (rec.station) col port di stationRowsToLandSamples;
// - GAP dichiarati: animazione onde marine, campioni CMEMS da griglia
//   (cmemsGridToMarineSamples, era solo per la vista forecast),
//   WSCoastExposure e filtro provincia attiva (assenti in v2).
import {
    THERMAL_IDW_PARAMS, LAND_IDW_PARAMS, MARINE_IDW_PARAMS,
    getStationParamValue, stationHasMetric, markerRowIsMarine,
    finiteValue, formatValue,
} from '../lib/params.js';
import { getColorScale, rainIntervalColor } from '../lib/scales.js';

// ======== Dominio fisso (condiviso con wslam.js) ============================
export const DOMAIN = { west: 11.8, south: 35.3, east: 16.2, north: 39.0, width: 1100, height: 900 };
export const DOMAIN_COORDS = [
    [DOMAIN.west, DOMAIN.north], [DOMAIN.east, DOMAIN.north],
    [DOMAIN.east, DOMAIN.south], [DOMAIN.west, DOMAIN.south],
];

function mercY(lat) {
    return Math.log(Math.tan((Math.PI / 4) + (lat * Math.PI / 360)));
}
const MERC_N = mercY(DOMAIN.north);
const MERC_S = mercY(DOMAIN.south);

// lat/lng → pixel del canvas-dominio (y lineare in Mercator, come la GPU).
export function domainPoint(lat, lng) {
    return {
        x: ((lng - DOMAIN.west) / (DOMAIN.east - DOMAIN.west)) * DOMAIN.width,
        y: ((MERC_N - mercY(lat)) / (MERC_N - MERC_S)) * DOMAIN.height,
    };
}

export function domainLatLng(x, y) {
    const lng = DOMAIN.west + (x / DOMAIN.width) * (DOMAIN.east - DOMAIN.west);
    const my = MERC_N - (y / DOMAIN.height) * (MERC_N - MERC_S);
    const lat = ((2 * Math.atan(Math.exp(my))) - (Math.PI / 2)) * 180 / Math.PI;
    return { lat, lng };
}

// ======== GeoJSON Sicilia + point-in-polygon (port map.js:609-640) ==========
let sicilyFeature = null;
let sicilyPromise = null;

export function loadSicilyFeature() {
    if (sicilyFeature) return Promise.resolve(sicilyFeature);
    if (!sicilyPromise) {
        // Stesso file della v1 (map.js:1259); assoluto perché la v2 vive in /v2/.
        sicilyPromise = fetch('/limits_IT_regions.geojson')
            .then(r => r.json())
            .then(geo => {
                sicilyFeature = geo.features.find(f => f.properties.reg_name === 'Sicilia') || null;
                return sicilyFeature;
            })
            .catch(() => { sicilyPromise = null; return null; });
    }
    return sicilyPromise;
}

export function getSicilyFeature() {
    return sicilyFeature;
}

function wsPointInRing(lat, lon, ring) {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const xi = Number(ring[i][0]);
        const yi = Number(ring[i][1]);
        const xj = Number(ring[j][0]);
        const yj = Number(ring[j][1]);
        const intersects = ((yi > lat) !== (yj > lat)) &&
            (lon < (xj - xi) * (lat - yi) / ((yj - yi) || Number.EPSILON) + xi);
        if (intersects) inside = !inside;
    }
    return inside;
}

function wsPointInPolygon(lat, lon, polygon) {
    if (!Array.isArray(polygon) || !polygon.length || !wsPointInRing(lat, lon, polygon[0])) return false;
    for (let i = 1; i < polygon.length; i++) {
        if (wsPointInRing(lat, lon, polygon[i])) return false;
    }
    return true;
}

export function featureContainsLatLng(feature, lat, lon) {
    const geometry = feature?.geometry || {};
    if (geometry.type === 'Polygon') return wsPointInPolygon(lat, lon, geometry.coordinates);
    if (geometry.type === 'MultiPolygon') {
        return (geometry.coordinates || []).some(polygon => wsPointInPolygon(lat, lon, polygon));
    }
    return false;
}

// ======== Maschera Sicilia rasterizzata (port sicilyClipRings + punch/keep) ==
// Anelli esterni come la v1 (sicilyClipRings: MultiPolygon → poly[0]).
function sicilyClipRings() {
    if (!sicilyFeature?.geometry) return [];
    return (sicilyFeature.geometry.type === 'MultiPolygon')
        ? sicilyFeature.geometry.coordinates.map(poly => poly[0]).filter(Boolean)
        : [sicilyFeature.geometry.coordinates[0]].filter(Boolean);
}

let maskCanvas = null;

export function sicilyMaskCanvas() {
    if (maskCanvas) return maskCanvas; // dominio fisso → mai da rigenerare
    const rings = sicilyClipRings();
    if (!rings.length) return null;
    const canvas = document.createElement('canvas');
    canvas.width = DOMAIN.width;
    canvas.height = DOMAIN.height;
    const ctx = canvas.getContext('2d', { alpha: true });
    ctx.beginPath();
    rings.forEach(ring => {
        ring.forEach(([lng, lat], index) => {
            const p = domainPoint(lat, lng);
            if (index === 0) ctx.moveTo(p.x, p.y);
            else ctx.lineTo(p.x, p.y);
        });
        ctx.closePath();
    });
    ctx.fillStyle = '#000';
    ctx.fill();
    maskCanvas = canvas;
    return canvas;
}

// op = 'destination-in' (tieni solo la terra) | 'destination-out' (fora la terra).
export function compositeSicilyMask(ctx, op) {
    const mask = sicilyMaskCanvas();
    if (!mask) return false;
    ctx.save();
    ctx.globalCompositeOperation = op;
    ctx.drawImage(mask, 0, 0);
    ctx.restore();
    return true;
}

// ======== Refresh di una source 'canvas' (animate:false) ====================
// Con animate:false MapLibre legge il canvas solo al mount: dopo un redraw
// play() riabilita la rilettura per il frame successivo, poi pause().
export function refreshCanvasSource(map, srcId) {
    const src = map?.getSource(srcId);
    if (!src) return;
    if (typeof src.play === 'function') {
        src.play();
        map.triggerRepaint();
        // A playing canvas keeps MapLibre busy, so waiting for idle can loop
        // forever. The render event follows the texture upload for this frame.
        map.once('render', () => { try { src.pause(); } catch (e) {} });
    } else {
        map.triggerRepaint();
    }
}

// ======== Costanti/ID ========================================================
const SRC_LAND = 'ws-interpol-land-src';
const LYR_LAND = 'ws-interpol-land';
const SRC_MARINE = 'ws-interpol-marine-src';
const LYR_MARINE = 'ws-interpol-marine';
const STATIONS_LYR = 'ws-stations-symbols';
const MOBILE_IDW_MAX_AGE_MS = 10 * 60 * 1000; // map-legend.js:1662
// Il canvas contiene già un alpha dipendente dalla copertura. Applicare anche
// un raster-opacity basso desaturava due volte il campo (0.78 × 0.62 < 0.49).
const LAND_CANVAS_OPACITY = 0.94;
const MARINE_CANVAS_OPACITY = 0.90;
const FIELD_MAX_ALPHA = 0.92;
const FIELD_ALPHA_SCALE = 0.90;

const state = {
    map: null,
    enabled: false,
    opacity: 1,
    parametro: 'temperatura',
    rows: [],            // row stazione grezzi (rec.station da 'wsv2:stations')
    landSamples: [],     // {lat,lng,value,name,source,valueLabel}
    marineData: [],      // port del mapping dati di createMarineTemperatureLayer
    landCanvas: null,
    marineCanvas: null,
    refreshGen: 0,
    landGen: 0,
    landTask: 0,
    popup: null,
};
const insetMaps = new Set();

function refreshInterpolationSources(srcId) {
    refreshCanvasSource(state.map, srcId);
    insetMaps.forEach(insetMap => refreshCanvasSource(insetMap, srcId));
}

export function landFieldStyle(parametro) {
    const palettes = window.WSPalettes;
    const key = palettes.paletteKeyForParam(parametro);
    const discrete = palettes.registry[key]?.type === 'rain';
    const scale = getColorScale(parametro);
    return {
        discrete,
        colorAt: discrete
            ? (value) => window.chroma(rainIntervalColor(value)).rgba()
            : (value) => scale(value).rgba(),
    };
}

export function interpolationLegendSpec(parametro) {
    const palettes = window.WSPalettes;
    if (!palettes) return null;
    const key = palettes.paletteKeyForParam ? palettes.paletteKeyForParam(parametro) : parametro;
    const def = palettes.registry?.[key];
    const domain = def?.domain || def?.breaks || [];
    const scale = palettes.scaleForParam?.(parametro);
    if (!domain.length || !scale) return null;
    const min = domain[0], max = domain[domain.length - 1];
    const bar = [];
    for (let i = 0; i <= 32; i += 1) {
        const value = min + ((max - min) * i / 32);
        let color = '#888888';
        try {
            const sampled = scale(value);
            color = sampled?.hex ? sampled.hex() : String(sampled);
        } catch (e) {}
        bar.push({ color, pos: i / 32 });
    }
    const ticks = [];
    for (let i = 0; i <= 4; i += 1) {
        const value = min + ((max - min) * i / 4);
        ticks.push({
            pos: i / 4,
            label: String(Math.abs(value) >= 100 ? Math.round(value) : Math.round(value * 10) / 10),
        });
    }
    const select = document.getElementById('selectParametro');
    const hours=/^rain(\d+)h$/.exec(parametro)?.[1];
    const label = hours ? `Pioggia cumulata · ultime ${hours} ore` : select?.selectedOptions?.[0]?.textContent?.trim() || parametro;
    if (def.type === 'rain') {
        const breaks = def.breaks;
        const sideBar = [], sideTicks = [];
        for (let i=0;i<breaks.length-1;i++) {
            const color=rainIntervalColor((breaks[i]+breaks[i+1])/2);
            sideBar.push({color,pos:i/(breaks.length-1)},{color,pos:(i+1)/(breaks.length-1)});
        }
        for (const value of [0,2,10,30,60,120,200,300,400,600]) {
            const index=breaks.indexOf(value);
            if(index>=0)sideTicks.push({pos:index/(breaks.length-1),label:String(value===max?'≥ '+max:value)});
        }
        return {title:`${label} · campo interpolato`,unit:legendUnit(parametro),bar:sideBar,ticks:sideTicks,side:true};
    }
    return { title: `${label} · campo interpolato`, unit: legendUnit(parametro), bar, ticks };
}

function publishInterpolationLegend(on = state.enabled) {
    document.dispatchEvent(new CustomEvent('wsv2:map-legend', {
        detail: { id: 'station-interpolation', spec: on ? interpolationLegendSpec(state.parametro) : null },
    }));
}

// ======== Unità legenda (port map-legend.js:1921) ============================
function legendUnit(parametro) {
    if (['temperatura', 'temp_mare', 'tmax', 'tmin', 'dewPoint', 'apparent', 'wetbulb', 'dewspread', 'windchill'].includes(parametro)) return '°C';
    if (parametro === 'umidita') return '%';
    if (['vento', 'raffica', 'vento_mare', 'raffica_mare'].includes(parametro)) return 'km/h';
    if (['rain', 'rainmonth', 'rainyear'].includes(parametro) || /^rain(?:1|3|6|12|24|48|72|96|120)h$/.test(parametro)) return 'mm';
    if (parametro === 'rainrate') return 'mm/h';
    if (parametro === 'neve') return 'cm';
    if (parametro === 'idro' || parametro === 'onda' || parametro === 'livello_mare') return 'm';
    if (parametro === 'periodo_onda') return 's';
    if (parametro === 'direzione_onda') return '°';
    if (parametro === 'corrente_mare') return 'm/s';
    if (parametro === 'pressione') return 'hPa';
    return '';
}

// ======== Sample builders (port map-legend.js:296-355, 262-294, 368-394) ====
function rowLatLng(row) {
    let lat = finiteValue(row?.lat);
    let lng = finiteValue(row?.lng ?? row?.lon);
    if ((lat === null || lng === null) && (row?.coordinate || row?.coordinates)) {
        const parts = String(row.coordinate || row.coordinates).split(',').map(value => finiteValue(value.trim()));
        if (parts.length >= 2) {
            lat = parts[0];
            lng = parts[1];
        }
    }
    return { lat, lng };
}

function stationRowsToLandSamples(rows, parametro) {
    if (!LAND_IDW_PARAMS.has(parametro)) return [];
    return (Array.isArray(rows) ? rows : [])
        .filter(row => !markerRowIsMarine(row))
        .filter(row => stationHasMetric(row, parametro))
        .map(row => {
            const { lat, lng } = rowLatLng(row);
            return {
                lat,
                lng,
                value: getStationParamValue(row, parametro),
                name: row.name || row.nome || 'Stazione',
                source: row.network_label || row.ente || row.tipo || 'Stazione',
            };
        })
        .filter(sample => sample.lat !== null && sample.lng !== null && sample.value !== null && sample.value > -99.9);
}

function marineValueForParam(row, parametro) {
    if (parametro === 'temperatura' || parametro === 'temp_mare') {
        return finiteValue(row?.temp_mare ?? row?.temperatura_mare ?? row?.sea_temperature ?? row?.temperatura ?? row?.temperature);
    }
    if (parametro === 'onda') {
        return finiteValue(row?.wave_height_m ?? row?.wave_height ?? row?.significant_wave_height ?? row?.swh ?? row?.sea_wave_height ?? row?.onda_altezza ?? row?.altezza_onda);
    }
    if (parametro === 'periodo_onda') {
        return finiteValue(row?.wave_period_s ?? row?.wave_period ?? row?.onda_periodo ?? row?.periodo_onda);
    }
    if (parametro === 'direzione_onda') {
        return finiteValue(row?.wave_direction ?? row?.wave_dir ?? row?.onda_direzione ?? row?.direzione_onda);
    }
    if (parametro === 'vento_mare') return finiteValue(row?.wind ?? row?.vento);
    if (parametro === 'raffica_mare') return finiteValue(row?.wind_gust ?? row?.rafficaMax ?? row?.raffica);
    if (parametro === 'corrente_mare') return finiteValue(row?.current_speed ?? row?.corrente ?? row?.current);
    if (parametro === 'livello_mare') return finiteValue(row?.idrometrico ?? row?.hydrometric_level ?? row?.livello_idrometrico);
    return null;
}

function marineValueInRange(value, parametro) {
    if (value === null || value <= -99.9) return false;
    if (parametro === 'temperatura' || parametro === 'temp_mare') return value >= -2 && value <= 45;
    if (parametro === 'onda') return value >= 0 && value < 25;
    if (parametro === 'periodo_onda') return value >= 0 && value < 40;
    if (parametro === 'direzione_onda') return value >= 0 && value <= 360;
    if (parametro === 'vento_mare' || parametro === 'raffica_mare') return value >= 0 && value < 250;
    if (parametro === 'corrente_mare') return value >= 0 && value < 8;
    if (parametro === 'livello_mare') return value > -10 && value < 10;
    return true;
}

// v1 live: le boe arrivano da WSWeatherApi.getBuoys() (drawIDW includeLiveMarine).
async function getMarineBuoySamples(parametro) {
    if (!window.WSWeatherApi?.getBuoys) return [];
    try {
        const buoys = await window.WSWeatherApi.getBuoys();
        return (Array.isArray(buoys) ? buoys : []).map(buoy => {
            const lat = finiteValue(buoy?.lat);
            const lon = finiteValue(buoy?.lng);
            const value = marineValueForParam(buoy, parametro);
            const waveHeight = finiteValue(buoy?.wave_height_m);
            const windKmh = finiteValue(buoy?.wind);
            if (lat === null || lon === null || !marineValueInRange(value, parametro)) return null;
            return {
                ...buoy,
                lat,
                lng: lon,
                value,
                temp_mare: parametro === 'temperatura' || parametro === 'temp_mare' ? value : finiteValue(buoy?.temp_mare),
                wave_height_m: waveHeight !== null && waveHeight >= 0 && waveHeight < 20 ? waveHeight : null,
                wind: windKmh !== null && windKmh >= 0 && windKmh < 250 ? windKmh : null,
            };
        }).filter(Boolean);
    } catch (e) {
        return [];
    }
}

// Mobile MeteoTracker per temperatura (port drawIDWFromRows includeMobile).
async function getMobileLandSamples() {
    if (!window.WSWeatherApi?.getMobileStations) return [];
    try {
        const mobiles = await window.WSWeatherApi.getMobileStations();
        const now = Date.now();
        const samples = [];
        (Array.isArray(mobiles) ? mobiles : []).forEach(m => {
            const { lat, lng } = rowLatLng(m);
            const t = finiteValue(m.temperatura);
            if (lat === null || lng === null || t === null || t <= -99.9) return;
            const ts = (typeof m.time_utc === 'number')
                ? (m.time_utc > 1e12 ? m.time_utc : m.time_utc * 1000)
                : Date.parse(m.time_utc || '');
            if (!isNaN(ts) && (now - ts) > MOBILE_IDW_MAX_AGE_MS) return;
            samples.push({ lat, lng, value: t, name: m.name || m.comune || 'MeteoTracker', source: 'Mobile' });
        });
        return samples;
    } catch (e) {
        return [];
    }
}

// Port del mapping dati in testa a createMarineTemperatureLayer.
function marineFieldData(rows) {
    return (Array.isArray(rows) ? rows : [])
        .map(row => ({
            id: row?.id,
            name: row?.name || 'Boa',
            lat: finiteValue(row?.lat),
            lng: finiteValue(row?.lng),
            value: finiteValue(row?.value ?? row?.temp_mare),
            waveHeight: finiteValue(row?.wave_height_m),
            windKmh: finiteValue(row?.wind),
            observedAt: row?.data || row?.last_update || row?.observed_at || '',
        }))
        .filter(row => row.lat !== null && row.lng !== null && row.value !== null)
        .map(row => ({
            ...row,
            temp: row.value,
            waveHeight: row.waveHeight !== null && row.waveHeight >= 0 && row.waveHeight < 20 ? row.waveHeight : null,
            windKmh: row.windKmh !== null && row.windKmh >= 0 && row.windKmh < 250 ? row.windKmh : null,
        }));
}

function buildMarineValueScale(_rows, parametro) {
    // Un solo significato cromatico in tutta la mappa: marker, campo
    // interpolato e legenda usano la stessa palette ufficiale del parametro.
    // Prima temp_mare ricostruiva una scala dinamica dai min/max delle boe:
    // lo stesso colore poteva quindi indicare temperature diverse a ogni
    // aggiornamento e non coincideva con i marker.
    return getColorScale(parametro);
}

// ======== Kernel IDW (parametri identici alla v1, spazio = dominio) =========
const KERNEL_RADIUS = Math.max(120, Math.min(280, DOMAIN.width * 0.28));
const KERNEL_SIGMA = KERNEL_RADIUS * 0.46;

export function landInterpolationSettings(parametro) {
    const rainfall = /^rain(?:rate|month|year|(?:1|3|6|12|24|48|72|96|120)h)?$/.test(parametro);
    return rainfall
        ? { cell: 2, distanceFloor: 0.25, radius: 60, maxNeighbors: 8 }
        : { cell: Math.max(12, Math.min(16, Math.round(DOMAIN.width / 64))), distanceFloor: 80 };
}


// Haversine come L.Map.distance (CRS.Earth, R=6371000).
function distanceMeters(lat1, lng1, lat2, lng2) {
    const rad = Math.PI / 180;
    const lat1r = lat1 * rad;
    const lat2r = lat2 * rad;
    const sinDLat = Math.sin((lat2 - lat1) * rad / 2);
    const sinDLon = Math.sin((lng2 - lng1) * rad / 2);
    const a = sinDLat * sinDLat + Math.cos(lat1r) * Math.cos(lat2r) * sinDLon * sinDLon;
    return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Port 1:1 di estimateWeightedSurface (map-legend.js:523) con
// latLngToContainerPoint → domainPoint (spazio dominio fisso).
export function estimateWeightedSurface(samples, latlng, options = {}) {
    if (!Array.isArray(samples) || !samples.length || !latlng) return null;

    const point = domainPoint(latlng.lat, latlng.lng);
    const radius = options.radius || KERNEL_RADIUS;
    const sigma = radius * (options.sigmaRatio || 0.46);
    let totalWeight = 0;
    let weightedValue = 0;
    let coverage = 0;

    let candidates = samples.map(sample => {
        const samplePoint = domainPoint(sample.lat, sample.lng);
        const dx = point.x - samplePoint.x;
        const dy = point.y - samplePoint.y;
        const d2 = dx * dx + dy * dy;
        // Local rain support: distant wet clusters must not outvote nearer dry gauges.
        if (options.maxNeighbors && d2 > 9 * sigma * sigma) return null;
        const gaussian = Math.exp(-d2 / (2 * sigma * sigma));
        const weight = gaussian / Math.max(options.distanceFloor ?? 80, d2);
        const meters = distanceMeters(latlng.lat, latlng.lng, sample.lat, sample.lng);

        return { ...sample, weight, gaussian, d2, distanceMeters: meters };
    }).filter(sample => sample && sample.weight > 0);
    if (options.maxNeighbors) candidates = candidates.sort((a,b) => a.d2-b.d2).slice(0,options.maxNeighbors);
    for (const sample of candidates) {
        coverage += sample.gaussian;
        weightedValue += sample.value * sample.weight;
        totalWeight += sample.weight;
    }
    const contributors = candidates;

    if (!totalWeight || coverage < (options.minCoverage || 0.015)) return null;

    return {
        value: weightedValue / totalWeight,
        coverage,
        contributors: contributors
            .sort((a, b) => b.weight - a.weight)
            .slice(0, options.maxContributors || 4)
            .map(sample => ({ ...sample, share: sample.weight / totalWeight })),
    };
}

// Port di MarineTemperatureLayer._estimateAt (con contributori) sul dominio.
function marineEstimateAtLatLng(latlng) {
    const data = state.marineData;
    if (!data.length || !latlng) return null;
    const point = domainPoint(latlng.lat, latlng.lng);
    const sigma = KERNEL_SIGMA;
    let totalWeight = 0;
    let weightedTemp = 0;
    let coverage = 0;

    const contributors = data.map(row => {
        const rowPoint = domainPoint(row.lat, row.lng);
        const dx = point.x - rowPoint.x;
        const dy = point.y - rowPoint.y;
        const d2 = dx * dx + dy * dy;
        const gaussian = Math.exp(-d2 / (2 * sigma * sigma));
        const weight = gaussian / Math.max(80, d2);

        coverage += gaussian;
        weightedTemp += row.value * weight;
        totalWeight += weight;

        return { ...row, weight, distanceMeters: distanceMeters(latlng.lat, latlng.lng, row.lat, row.lng) };
    }).filter(row => row.weight > 0);

    if (!totalWeight || coverage < 0.015) return null;

    return {
        value: weightedTemp / totalWeight,
        coverage,
        contributors: contributors
            .sort((a, b) => b.weight - a.weight)
            .slice(0, 4)
            .map(row => ({ ...row, share: row.weight / totalWeight })),
    };
}

// Port di _estimateProjectedAt col bucket spaziale (map-legend.js:1548).
export function estimateProjectedAt(point, spatial, bucketSize, sigma, cutoff2, distanceFloor = 80, maxNeighbors = 0) {
    let totalWeight = 0;
    let weightedValue = 0;
    let coverage = 0;
    const twoSigma2 = 2 * sigma * sigma;
    const nearest = maxNeighbors > 0 ? [] : null;
    const bx = Math.floor(point.x / bucketSize);
    const by = Math.floor(point.y / bucketSize);

    for (let gx = bx - 1; gx <= bx + 1; gx += 1) {
        for (let gy = by - 1; gy <= by + 1; gy += 1) {
            const bucket = spatial.get(`${gx},${gy}`);
            if (!bucket) continue;
            for (let i = 0; i < bucket.length; i += 1) {
                const row = bucket[i];
                const dx = point.x - row.point.x;
                const dy = point.y - row.point.y;
                const d2 = dx * dx + dy * dy;
                if (d2 > cutoff2) continue;
                if (nearest) {
                    let at = nearest.length;
                    while (at > 0 && nearest[at-1].d2 > d2) at--;
                    if (at < maxNeighbors) {
                        nearest.splice(at, 0, {row,d2});
                        if (nearest.length > maxNeighbors) nearest.pop();
                    }
                    continue;
                }
                const gaussian = Math.exp(-d2 / twoSigma2);
                const weight = gaussian / Math.max(distanceFloor, d2);
                coverage += gaussian;
                weightedValue += row.value * weight;
                totalWeight += weight;
            }
        }
    }

    if (nearest) for (const {row,d2} of nearest) {
        const gaussian = Math.exp(-d2 / twoSigma2);
        const weight = gaussian / Math.max(distanceFloor,d2);
        coverage += gaussian;
        weightedValue += row.value * weight;
        totalWeight += weight;
    }
    if (!totalWeight || coverage < 0.015) return null;
    return { value: weightedValue / totalWeight, coverage };
}

export function buildSpatialIndex(projected, bucketSize) {
    const index = new Map();
    for (let i = 0; i < projected.length; i += 1) {
        const row = projected[i];
        const bx = Math.floor(row.point.x / bucketSize);
        const by = Math.floor(row.point.y / bucketSize);
        const key = `${bx},${by}`;
        let bucket = index.get(key);
        if (!bucket) { bucket = []; index.set(key, bucket); }
        bucket.push(row);
    }
    return index;
}

// ======== Render campo TERRA (port LandBilinearLayer._draw) =================
function renderLandField() {
    const canvas = state.landCanvas;
    if (!canvas) return;
    const gen = (state.landGen += 1);
    if (state.landTask) { clearTimeout(state.landTask); state.landTask = 0; }
    const samples = state.landSamples;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!samples.length) {
        ctx.clearRect(0, 0, DOMAIN.width, DOMAIN.height);
        refreshInterpolationSources(SRC_LAND);
        return;
    }

    const parametro = state.parametro;
    // Follow the parameter's registered palette. Annual rainfall is a gradient
    // over 0–1600 mm; applying daily-rain bands would saturate it above 300 mm.
    const { colorAt: rawColorAt, discrete } = landFieldStyle(parametro);
    const colors = new Map();
    const colorAt = discrete ? (value) => {
        const key = rainIntervalColor(value);
        if (!colors.has(key)) colors.set(key, window.chroma(key).rgba());
        return colors.get(key);
    } : rawColorAt;

    const size = { x: DOMAIN.width, y: DOMAIN.height };
    const { cell, distanceFloor, radius = KERNEL_RADIUS, maxNeighbors = 0 } = landInterpolationSettings(parametro);
    const cols = Math.max(1, Math.ceil(size.x / cell));
    const rows = Math.max(1, Math.ceil(size.y / cell));
    const fieldCanvas = document.createElement('canvas');
    fieldCanvas.width = cols;
    fieldCanvas.height = rows;
    const fieldCtx = fieldCanvas.getContext('2d', { alpha: true });
    const projected = samples.map(sample => ({ ...sample, point: domainPoint(sample.lat, sample.lng) }));
    const sigma = radius * 0.46;
    const cutoff = sigma * 3.0;
    const cutoff2 = cutoff * cutoff;
    const bucketSize = Math.max(1, cutoff);
    const spatial = buildSpatialIndex(projected, bucketSize);

    let rowIndex = 0;
    const finish = () => {
        ctx.clearRect(0, 0, size.x, size.y);
        // Smooth the display between grid cells, including rainfall interval colors.
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(fieldCanvas, 0, 0, size.x, size.y);
        compositeSicilyMask(ctx, 'destination-in'); // = keepOnlySicilyLandOnCanvas v1
        refreshInterpolationSources(SRC_LAND);
    };
    // Compute in short tasks independently of map animation frames. A slow GPU
    // must not postpone a finished field for dozens of seconds.
    const step = () => {
        if (gen !== state.landGen) return;
        const t0 = performance.now();
        while (rowIndex < rows) {
            for (let colIndex = 0; colIndex < cols; colIndex += 1) {
                const point = { x: (colIndex + 0.5) * size.x / cols, y: (rowIndex + 0.5) * size.y / rows };
                const estimate = estimateProjectedAt(point, spatial, bucketSize, sigma, cutoff2, distanceFloor, maxNeighbors);
                if (!estimate) continue;
                const alpha = Math.min(FIELD_MAX_ALPHA, Math.pow(Math.min(1, estimate.coverage * 1.65), 0.82) * FIELD_ALPHA_SCALE);
                if (alpha < 0.035) continue;
                const color = colorAt(estimate.value);
                fieldCtx.fillStyle = `rgba(${Math.round(color[0])},${Math.round(color[1])},${Math.round(color[2])},${alpha.toFixed(3)})`;
                fieldCtx.fillRect(colIndex, rowIndex, 1, 1);
            }
            rowIndex += 1;
            if (performance.now() - t0 > 10) break;
        }
        if (rowIndex < rows) state.landTask = setTimeout(step, 0);
        else { state.landTask = 0; finish(); }
    };
    state.landTask = setTimeout(step, 0);
}

// ======== Render campo MARE (port MarineTemperatureLayer._renderField) ======
// Senza animazione onde (fase successiva): solo il campo colore.
function renderMarineField() {
    const canvas = state.marineCanvas;
    if (!canvas) return;
    const data = state.marineData;
    const ctx = canvas.getContext('2d', { alpha: true });
    ctx.clearRect(0, 0, DOMAIN.width, DOMAIN.height);
    if (!data.length) {
        refreshInterpolationSources(SRC_MARINE);
        return;
    }

    const scale = buildMarineValueScale(data, state.parametro);
    const size = { x: DOMAIN.width, y: DOMAIN.height };
    const cell = Math.max(12, Math.min(16, Math.round(size.x / 64)));
    const cols = Math.max(1, Math.ceil(size.x / cell));
    const rows = Math.max(1, Math.ceil(size.y / cell));
    const fieldCanvas = document.createElement('canvas');
    fieldCanvas.width = cols;
    fieldCanvas.height = rows;
    const fieldCtx = fieldCanvas.getContext('2d', { alpha: true });
    const projected = data.map(row => ({ ...row, point: domainPoint(row.lat, row.lng) }));
    const sigma = KERNEL_SIGMA;

    for (let rowIndex = 0; rowIndex < rows; rowIndex += 1) {
        for (let colIndex = 0; colIndex < cols; colIndex += 1) {
            const cx = (colIndex + 0.5) * cell;
            const cy = (rowIndex + 0.5) * cell;
            // Port di _estimateProjectedAt marino (tutte le boe, no indice).
            let totalWeight = 0;
            let weightedTemp = 0;
            let coverage = 0;
            projected.forEach(row => {
                const dx = cx - row.point.x;
                const dy = cy - row.point.y;
                const d2 = dx * dx + dy * dy;
                const gaussian = Math.exp(-d2 / (2 * sigma * sigma));
                const weight = gaussian / Math.max(80, d2);
                coverage += gaussian;
                weightedTemp += row.temp * weight;
                totalWeight += weight;
            });
            if (!totalWeight || coverage < 0.015) continue;
            const value = weightedTemp / totalWeight;
            const alpha = Math.min(FIELD_MAX_ALPHA, Math.pow(Math.min(1, coverage * 1.65), 0.82) * FIELD_ALPHA_SCALE);
            if (alpha < 0.035) continue;
            const color = scale(value).rgba();
            fieldCtx.fillStyle = `rgba(${Math.round(color[0])},${Math.round(color[1])},${Math.round(color[2])},${alpha.toFixed(3)})`;
            fieldCtx.fillRect(colIndex, rowIndex, 1, 1);
        }
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(fieldCanvas, 0, 0, size.x, size.y);
    compositeSicilyMask(ctx, 'destination-out'); // = punchSicilyLandFromCanvas v1
    refreshInterpolationSources(SRC_MARINE);
}

// ======== Sorgenti/layer MapLibre ===========================================
function ensureCanvases() {
    if (!state.landCanvas) {
        state.landCanvas = document.createElement('canvas');
        state.landCanvas.width = DOMAIN.width;
        state.landCanvas.height = DOMAIN.height;
    }
    if (!state.marineCanvas) {
        state.marineCanvas = document.createElement('canvas');
        state.marineCanvas.width = DOMAIN.width;
        state.marineCanvas.height = DOMAIN.height;
    }
}

function ensureLayersOnMap(map) {
    if (!map) return;
    ensureCanvases();
    const before = map.getLayer(STATIONS_LYR)
        ? STATIONS_LYR
        : (map.getLayer('ins-st') ? 'ins-st' : undefined);
    if (!map.getSource(SRC_MARINE)) {
        map.addSource(SRC_MARINE, { type: 'canvas', canvas: state.marineCanvas, coordinates: DOMAIN_COORDS, animate: false });
    }
    if (!map.getLayer(LYR_MARINE)) {
        map.addLayer({
            id: LYR_MARINE, type: 'raster', source: SRC_MARINE,
            paint: { 'raster-opacity': MARINE_CANVAS_OPACITY * state.opacity, 'raster-fade-duration': 0, 'raster-resampling': 'linear' },
        }, before);
    }
    if (!map.getSource(SRC_LAND)) {
        map.addSource(SRC_LAND, { type: 'canvas', canvas: state.landCanvas, coordinates: DOMAIN_COORDS, animate: false });
    }
    if (!map.getLayer(LYR_LAND)) {
        // terra SOPRA il mare (ordine overlayPane v1), entrambi sotto le stazioni
        map.addLayer({
            id: LYR_LAND, type: 'raster', source: SRC_LAND,
            paint: { 'raster-opacity': LAND_CANVAS_OPACITY * state.opacity, 'raster-fade-duration': 0, 'raster-resampling': 'linear' },
        }, before);
    }
}

function ensureLayers() {
    ensureLayersOnMap(state.map);
    enforceTerrainReliefOrder(state.map);
}

function removeLayersFromMap(map) {
    if (!map) return;
    for (const id of [LYR_LAND, LYR_MARINE]) {
        try { if (map.getLayer(id)) map.removeLayer(id); } catch (e) {}
    }
    for (const id of [SRC_LAND, SRC_MARINE]) {
        try { if (map.getSource(id)) map.removeSource(id); } catch (e) {}
    }
}

function removeLayers() {
    removeLayersFromMap(state.map);
}

// ======== Rebuild dati + render (port drawIDWFromRows, live path) ===========
async function refresh() {
    if (!state.enabled) return;
    const gen = (state.refreshGen += 1);
    const parametro = state.parametro;

    if (!THERMAL_IDW_PARAMS.has(parametro)) {
        state.landSamples = [];
        state.marineData = [];
        renderLandField();
        renderMarineField();
        return;
    }

    const landSamples = stationRowsToLandSamples(state.rows, parametro);
    if (parametro === 'temperatura') {
        landSamples.push(...await getMobileLandSamples());
    }

    let marineSamples = [];
    // La temperatura dell'aria delle stazioni terrestri e la temperatura
    // superficiale del mare sono grandezze diverse: sovrapporle sotto un'unica
    // scala produceva una falsa continuità lungo la costa e vaste chiazze in
    // mare. Il campo marino si calcola solo con un parametro marino esplicito.
    if (MARINE_IDW_PARAMS.has(parametro)) {
        marineSamples = await getMarineBuoySamples(parametro); // includeLiveMarine v1
    }
    if (gen !== state.refreshGen || !state.enabled) return;

    const unit = legendUnit(parametro);
    state.landSamples = landSamples.map(sample => ({
        ...sample,
        valueLabel: `${formatValue(parametro, sample.value)}${unit ? ' ' + unit : ''}`,
    }));
    state.marineData = marineFieldData(marineSamples);

    renderLandField();
    renderMarineField();
}

// ======== Popup click (port showInterpolationPopup + pinnedPointReadout) ====
function esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function formatDistance(meters) {
    if (!Number.isFinite(meters)) return '';
    return meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)} m`;
}

function contributorsHtml(contributors, parametro, unit) {
    const items = (contributors || []).map(c => {
        const label = c.valueLabel || `${formatValue(parametro, c.value)}${unit ? ' ' + unit : ''}`;
        const share = Math.round((c.share || 0) * 100);
        const dist = formatDistance(c.distanceMeters);
        return `<li><strong>${esc(c.name)}</strong> — ${esc(label)} · ${share}%${dist ? ' · ' + esc(dist) : ''}</li>`;
    }).join('');
    return items ? `<ul class="wsv2-interpol-popup__contributors">${items}</ul>` : '';
}

function clickHitsStations(e) {
    const map = state.map;
    if (!map.getLayer(STATIONS_LYR)) return false;
    try {
        return map.queryRenderedFeatures(e.point, { layers: [STATIONS_LYR] }).length > 0;
    } catch (err) {
        return false;
    }
}

function onMapClick(e) {
    if (!state.enabled) return;
    // Se il campo WSLAM è attivo decide lui (parità pinnedPointReadout v1).
    if (window.wsv2?.wslamActive) return;
    if (clickHitsStations(e)) return;

    const latlng = { lat: e.lngLat.lat, lng: e.lngLat.lng };
    const parametro = state.parametro;
    const unit = legendUnit(parametro);
    const isLand = sicilyFeature ? featureContainsLatLng(sicilyFeature, latlng.lat, latlng.lng) : false;

    let title = '';
    let estimate = null;
    if (isLand && state.landSamples.length) {
        estimate = estimateWeightedSurface(state.landSamples, latlng, { ...landInterpolationSettings(parametro), maxContributors: 4, minCoverage: 0.015 });
        title = 'Interpolazione isola';
    } else if (!isLand && state.marineData.length) {
        estimate = marineEstimateAtLatLng(latlng);
        title = 'Interpolazione mare';
    }
    if (!estimate) return;

    const valueLabel = `${formatValue(parametro, estimate.value)}${unit ? ' ' + unit : ''}`;
    const html = `
<div class="wsv2-interpol-popup">
  <div class="wsv2-interpol-popup__title">${esc(title)}</div>
  <div class="wsv2-interpol-popup__value">${esc(valueLabel)}</div>
  ${contributorsHtml(estimate.contributors, parametro, unit)}
</div>`.trim();

    if (!state.popup) {
        state.popup = new maplibregl.Popup({ closeButton: true, closeOnClick: true, maxWidth: '280px', className: 'wsv2-interpol-popup-shell' });
    }
    state.popup.setLngLat(e.lngLat).setHTML(html).addTo(state.map);
}

// ======== API pubblica =======================================================
export function initInterpol(map) {
    state.map = map;
    if (window.wsv2) window.wsv2.interpolation = { getDiagnostics: () => ({
        enabled: state.enabled, parametro: state.parametro, rows: state.rows.length,
        samples: state.landSamples.length, drySamples: state.landSamples.filter(s => s.value === 0).length, generation: state.landGen, rendering: !!state.landTask,
        mask: !!maskCanvas, rings: sicilyClipRings().length,
    }) };
    document.addEventListener('wsv2:stations', (e) => {
        // The observation event is authoritative for both data and parameter.
        // URL initialization can change stations without a select change event;
        // never interpolate those rows with the previous parameter/palette.
        const parametro = e.detail?.parametro;
        if (parametro && parametro !== state.parametro) {
            state.parametro = parametro;
            if (state.enabled) publishInterpolationLegend(true);
        }
        setInterpolRecords(e.detail?.interpolationRecords ?? e.detail?.records ?? []);
    });
    map.on('click', onMapClick);
}

// Pantelleria e Pelagie vivono in istanze MapLibre autonome. Registrandole qui
// riusano lo stesso canvas georiferito, la stessa maschera e la stessa palette
// della mappa principale, senza eseguire una seconda interpolazione.
export function registerInterpolInset(insetMap) {
    if (!insetMap) return () => {};
    insetMaps.add(insetMap);
    if (state.enabled) ensureLayersOnMap(insetMap);
    const cleanup = () => insetMaps.delete(insetMap);
    insetMap.once('remove', cleanup);
    return cleanup;
}

export function isInterpolationEnabled(){return state.enabled;}

export async function setInterpolEnabled(on) {
    if (on === state.enabled) return;
    state.enabled = on;
    document.dispatchEvent(new CustomEvent('wsv2:interpolation-change',{detail:{enabled:on}}));
    if (on) {
        publishInterpolationLegend(true);
        await loadSicilyFeature(); // serve per maschera + instradamento click
        if (!state.enabled) return; // spento nel frattempo
        ensureLayers();
        insetMaps.forEach(ensureLayersOnMap);
        await refresh();
    } else {
        publishInterpolationLegend(false);
        state.refreshGen += 1;
        state.landGen += 1;
        if (state.landTask) { clearTimeout(state.landTask); state.landTask = 0; }
        if (state.popup) state.popup.remove();
        removeLayers();
        insetMaps.forEach(removeLayersFromMap);
    }
}

export function setInterpolParametro(p) {
    if (!p || p === state.parametro) return;
    state.parametro = p;
    if (state.enabled) {
        publishInterpolationLegend(true);
        refresh();
    }
}

// Accetta sia i record di stations.js ({..., station: row}) sia row grezzi.
export function setInterpolRecords(records) {
    state.rows = (Array.isArray(records) ? records : [])
        .map(rec => rec?.station || rec)
        .filter(Boolean);
    if (state.enabled) refresh();
}

// Presentation only: estimates, station weights and the legend stay unchanged.
export function setInterpolOpacity(value) {
    if (!Number.isFinite(Number(value))) return;
    state.opacity = Math.max(0, Math.min(1, Number(value)));
    for (const map of [state.map, ...insetMaps]) {
        if (!map) continue;
        for (const [id, opacity] of [[LYR_LAND, LAND_CANVAS_OPACITY], [LYR_MARINE, MARINE_CANVAS_OPACITY]]) {
            if (map.getLayer(id)) map.setPaintProperty(id, 'raster-opacity', opacity * state.opacity);
        }
    }
}
